import { motion, AnimatePresence } from 'framer-motion';
import { X, LucideIcon, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

interface UseCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  long: string;
  benefits: string[];
  icon: LucideIcon;
  color: 'blue' | 'red' | 'green' | 'purple' | 'orange' | 'teal' | 'indigo' | 'cyan';
}

const colorStyles = {
  blue: {
    gradient: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50 dark:bg-blue-950/30',
    borderLight: 'border-blue-200 dark:border-blue-800',
    textLight: 'text-blue-700 dark:text-blue-300'
  },
  red: {
    gradient: 'from-red-500 to-red-600',
    bgLight: 'bg-red-50 dark:bg-red-950/30',
    borderLight: 'border-red-200 dark:border-red-800',
    textLight: 'text-red-700 dark:text-red-300'
  },
  green: {
    gradient: 'from-green-500 to-green-600',
    bgLight: 'bg-green-50 dark:bg-green-950/30',
    borderLight: 'border-green-200 dark:border-green-800',
    textLight: 'text-green-700 dark:text-green-300'
  },
  purple: {
    gradient: 'from-purple-500 to-purple-600',
    bgLight: 'bg-purple-50 dark:bg-purple-950/30',
    borderLight: 'border-purple-200 dark:border-purple-800',
    textLight: 'text-purple-700 dark:text-purple-300'
  },
  orange: {
    gradient: 'from-orange-500 to-orange-600',
    bgLight: 'bg-orange-50 dark:bg-orange-950/30',
    borderLight: 'border-orange-200 dark:border-orange-800',
    textLight: 'text-orange-700 dark:text-orange-300'
  },
  teal: {
    gradient: 'from-teal-500 to-teal-600',
    bgLight: 'bg-teal-50 dark:bg-teal-950/30',
    borderLight: 'border-teal-200 dark:border-teal-800',
    textLight: 'text-teal-700 dark:text-teal-300'
  },
  indigo: {
    gradient: 'from-indigo-500 to-indigo-600',
    bgLight: 'bg-indigo-50 dark:bg-indigo-950/30',
    borderLight: 'border-indigo-200 dark:border-indigo-800',
    textLight: 'text-indigo-700 dark:text-indigo-300'
  },
  cyan: {
    gradient: 'from-cyan-500 to-cyan-600',
    bgLight: 'bg-cyan-50 dark:bg-cyan-950/30',
    borderLight: 'border-cyan-200 dark:border-cyan-800',
    textLight: 'text-cyan-700 dark:text-cyan-300'
  }
};

export function UseCaseModal({ isOpen, onClose, title, long, benefits, icon: Icon, color }: UseCaseModalProps) {
  const { t } = useTranslation('home');
  const styles = colorStyles[color];
  const [showVideo, setShowVideo] = useState(false);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (showVideo) {
          setShowVideo(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, showVideo]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset video state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowVideo(false);
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-modal-overlay"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-modal-content flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
                width: showVideo ? '100%' : 'auto',
                maxWidth: showVideo ? '1200px' : '512px' // max-w-2xl
              }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <AnimatePresence mode="wait">
                {!showVideo ? (
                  // Detail View
                  <motion.div
                    key="detail"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="h-full flex flex-col"
                  >
                    {/* Header */}
                    <div className={`${styles.bgLight} p-6 border-b ${styles.borderLight}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`inline-flex p-3 rounded-lg bg-gradient-to-br ${styles.gradient} shadow-lg`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h2 className={`text-2xl font-bold ${styles.textLight}`}>
                              {title}
                            </h2>
                          </div>
                        </div>
                        <button
                          onClick={onClose}
                          className="shrink-0 p-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-all hover:scale-110 shadow-md"
                          aria-label="Close"
                        >
                          <X className="w-7 h-7 text-black dark:text-white stroke-[2.5]" />
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                      {/* Long Description */}
                      <div className="mb-6">
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                          {long}
                        </p>
                      </div>

                      {/* Benefits */}
                      <div>
                        <h3 className={`text-lg font-semibold mb-3 ${styles.textLight}`}>
                          {t('useCases.modal.benefitsTitle')}
                        </h3>
                        <ul className="space-y-2">
                          {benefits.map((benefit, index) => (
                            <motion.li
                              key={index}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className="flex items-start gap-3"
                            >
                              <div className={`mt-1 w-1.5 h-1.5 rounded-full ${styles.gradient} bg-gradient-to-br flex-shrink-0`}></div>
                              <span className="text-gray-700 dark:text-gray-300">
                                {benefit}
                              </span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className={`p-4 border-t ${styles.borderLight} ${styles.bgLight}`}>
                      <button
                        onClick={() => setShowVideo(true)}
                        className={`w-full py-3 px-6 rounded-lg bg-gradient-to-r ${styles.gradient} text-white font-semibold hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2`}
                      >
                        <Play className="w-5 h-5" />
                        {t('useCases.modal.watchDemo')}
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  // Video View
                  <motion.div
                    key="video"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="h-full flex flex-col"
                  >
                    {/* Video Header */}
                    <div className={`${styles.bgLight} p-4 border-b ${styles.borderLight}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`inline-flex p-2 rounded-lg bg-gradient-to-br ${styles.gradient} shadow-lg`}>
                            <Play className="w-5 h-5 text-white" />
                          </div>
                          <h2 className={`text-xl font-bold ${styles.textLight}`}>
                            {t('useCases.modal.demoTitle')} - {title}
                          </h2>
                        </div>
                        <button
                          onClick={() => setShowVideo(false)}
                          className="shrink-0 p-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-all hover:scale-110 shadow-md"
                          aria-label="Back to details"
                        >
                          <X className="w-7 h-7 text-black dark:text-white stroke-[2.5]" />
                        </button>
                      </div>
                    </div>

                    {/* Video Content */}
                    <div className="flex-1 p-6 overflow-y-auto">
                      <div className="aspect-video bg-black rounded-lg flex items-center justify-center relative overflow-hidden">
                        {/* Placeholder for video player */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                          <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${styles.gradient} flex items-center justify-center mb-4 shadow-2xl`}>
                            <Play className="w-10 h-10" />
                          </div>
                          <p className="text-lg font-semibold mb-2">
                            {t('useCases.modal.videoComingSoon')}
                          </p>
                          <p className="text-sm text-gray-400 text-center max-w-md">
                            {t('useCases.modal.videoPlaceholder').replace('{title}', title)}
                          </p>
                        </div>
                      </div>

                      {/* Video Info */}
                      <div className="mt-6">
                        <h3 className={`text-lg font-semibold mb-2 ${styles.textLight}`}>
                          {title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('useCases.modal.videoDescription')}
                        </p>
                      </div>
                    </div>

                    {/* Video Footer */}
                    <div className={`p-4 border-t ${styles.borderLight} ${styles.bgLight}`}>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setShowVideo(false)}
                          className="flex-1 py-3 px-6 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
                        >
                          {t('useCases.modal.backToDetails')}
                        </button>
                        <button
                          onClick={onClose}
                          className={`flex-1 py-3 px-6 rounded-lg bg-gradient-to-r ${styles.gradient} text-white font-semibold hover:shadow-lg transition-all duration-200`}
                        >
                          {t('useCases.modal.close')}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
